import { Button, Message, Spinner } from "@adfenix/alexandria";
import { ProgramService, PropertyService, ReadLayer } from "@adfenix/types";
import { ReadLayerResult } from "api/services";
import { ReadLayerContext } from "components/ReadLayer/ReadLayerQueryContext";
import { useContext } from "react";
import styled from "styled-components";
import { SelectedSubject } from "./TemplatePreviewModal";

const StyledButton = styled(Button)`
  margin-left: 16px;
`;

const StyledTable = styled.table`
  td {
    vertical-align: middle;
  }
`;

export const SubjectTable = ({
  subjectType,
  setItem,
  templateType
}: {
  subjectType: ProgramService.SubjectType;
  setItem: (x: SelectedSubject) => void;
  templateType: "ad" | "image";
}) => {
  switch (subjectType) {
    case "Agent":
      return (
        <SubjectItems<ReadLayer.AgentSearchModel>
          templateType={templateType}
          setSearch={setItem}
          getImageKey={x => x.imageUrl}
          getLabelKey={x => x.emailAddress}
        />
      );

    case "Property":
      return (
        <SubjectItems<PropertyService.PropertyWithCampaignsSearchModel>
          templateType={templateType}
          setSearch={setItem}
          getImageKey={x => x.mainImageUrl}
          getLabelKey={x => x.location.address}
        />
      );

    case "Office":
      return (
        <SubjectItems<ReadLayer.OfficeSearchModel>
          templateType={templateType}
          setSearch={setItem}
          getImageKey={x => x.imageUrl}
          getLabelKey={x => x.name}
        />
      );
  }
};

function SubjectItems<
  T extends
    | ReadLayer.AgentSearchModel
    | PropertyService.PropertyWithCampaignsSearchModel
    | ReadLayer.OfficeSearchModel
>({
  setSearch,
  getImageKey,
  getLabelKey,
  templateType
}: {
  setSearch: (x: SelectedSubject) => void;
  getImageKey: (item: T) => string;
  getLabelKey: (Item: T) => string;
  templateType: "ad" | "image";
}) {
  const ctx = useContext(ReadLayerContext);
  const items = (ctx?.data as ReadLayerResult<T>)?.documents;
  if (!items || ctx?.isLoading) return <Spinner />;

  const filteredItems = items
    .filter(x => (templateType === "image" ? getImageKey(x) : x))
    .filter(x => !!getLabelKey(x));

  return (
    <>
      <StyledTable className="table">
        <tbody>
          {filteredItems.map(x => (
            <tr key={x.id}>
              <td>{getLabelKey(x)}</td>
              <td>
                <StyledButton
                  logLabel="preview-template-button"
                  onClick={() => {
                    setSearch({
                      id: x.id,
                      image: getImageKey(x)
                    });
                  }}
                >
                  Preview
                </StyledButton>
              </td>
            </tr>
          ))}
        </tbody>
      </StyledTable>
      <Message type="info">
        Items that are missing relevant properties such as images or
        address/emailaddress/name (depending on the subject type) will be
        filtered out.
      </Message>
    </>
  );
}
